import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { API_URL } from "../config/api";

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";
const MAX_ACTIVE_FILTERS = 2;

const SORT_COLUMN_MAP = {
  freightNumber: "freightNumber",
  customerName: "customerName",
  freightAmount: "freightAmount",
  totalPoValue: "totalPoValue",
  freightQty: "freightQty",
  supplierName: "supplierName",
  freightStatus: "freightStatus",
  createdAt: "createdAt",
};

const DEFAULT_FILTER_CHECKS = {
  customer: false,
  supplier: false,
  poNumber: false,
  createdBy: false,
  dateRange: false,
};
const DEFAULT_PO_NUMBER_PREFIX = "PO-ITS-";
const DEFAULT_FILTER_INPUTS = {
  customer: "",
  supplier: "",
  poNumber: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  customer: "",
  supplier: "",
  poNumber: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};

const FREIGHT_FILTER_OPTIONS = [
  { key: "customer", id: "freightFilterCheckCustomer", label: "Customer" },
  { key: "supplier", id: "freightFilterCheckSupplier", label: "Supplier" },
  { key: "poNumber", id: "freightFilterCheckPoNumber", label: "PO Number" },
  {
    key: "createdBy",
    id: "freightFilterCheckCreatedBy",
    label: "Created User",
  },
  { key: "dateRange", id: "freightFilterCheckDateRange", label: "Date" },
];

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractFreightPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { freight: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      freight: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list =
    dataBlock.freight ||
    dataBlock.freights ||
    dataBlock.items ||
    root.freight;

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : undefined;

  return {
    freight: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
  };
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")}`;
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCompactAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  const absNum = Math.abs(num);
  if (absNum >= 1e7) return `₹${(num / 1e7).toFixed(2)}Cr`;
  if (absNum >= 1e5) return `₹${(num / 1e5).toFixed(2)}L`;
  if (absNum >= 1e3) return `₹${(num / 1e3).toFixed(2)}K`;
  return `₹${formatAmount(num)}`;
}

function formatFullAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return `₹${formatAmount(num)}`;
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function displayUser(ref) {
  if (!ref) return "-";
  if (typeof ref === "string") return ref;
  return ref.name || ref.email || "-";
}

function getPoNumbers(row) {
  const list = Array.isArray(row?.poNumbers) ? row.poNumbers : [];
  return list.map((p) => String(p || "").trim()).filter(Boolean);
}

function getSupplierNames(row) {
  const list = Array.isArray(row?.suppliers) ? row.suppliers : [];
  const fromArray = list
    .map((s) =>
      String(
        (typeof s === "string" ? s : s?.supplierName || s?.name || "") || "",
      ).trim(),
    )
    .filter(Boolean);
  if (fromArray.length > 0) return fromArray;

  const legacy = String(row?.supplierName || "").trim();
  return legacy ? [legacy] : [];
}

function extractCustomerOptions(responseData) {
  const root = responseData?.data != null ? responseData.data : responseData;
  const list = Array.isArray(root)
    ? root
    : Array.isArray(root?.customers)
      ? root.customers
      : Array.isArray(root?.data)
        ? root.data
        : Array.isArray(responseData?.customers)
          ? responseData.customers
          : [];

  return list
    .map((item) => ({
      id: String(item?._id || item?.id || "").trim(),
      name: String(
        item?.customerName || item?.customer || item?.name || "",
      ).trim(),
    }))
    .filter((item) => item.id && item.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function extractSupplierOptions(responseData) {
  const list = Array.isArray(responseData?.suppliers)
    ? responseData.suppliers
    : Array.isArray(responseData?.data?.suppliers)
      ? responseData.data.suppliers
      : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

  return list
    .map((item) => ({
      id: String(item?._id || item?.id || "").trim(),
      name: String(
        item?.clientname || item?.clientName || item?.name || "",
      ).trim(),
    }))
    .filter((item) => item.id && item.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function extractUserOptions(responseData) {
  const list = Array.isArray(responseData?.users)
    ? responseData.users
    : Array.isArray(responseData?.data?.users)
      ? responseData.data.users
      : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

  return list
    .map((item) => ({
      id: String(item?._id || item?.id || "").trim(),
      name: String(
        item?.name || item?.userName || item?.email || "",
      ).trim(),
    }))
    .filter((item) => item.id && item.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function freightStatusBadge(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  let badge = "badge-secondary";
  if (normalized === "pending") badge = "badge-warning";
  if (normalized === "paid") badge = "badge-success";
  if (normalized === "cancelled" || normalized === "canceled") {
    badge = "badge-danger";
  }
  return (
    <span className={`badge ${badge}`} style={{ fontWeight: 550 }}>
      {capitalizeStatus(status)}
    </span>
  );
}

const FREIGHT_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterKey: null,
    filterValue: null,
  },
  {
    key: "activeCount",
    amountKey: "activeAmount",
    label: "Active",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterKey: "status",
    filterValue: "active",
  },
  {
    key: "inactiveCount",
    label: "Inactive",
    titleColor: "#7B241C",
    valueColor: "#C0392B",
    filterKey: "status",
    filterValue: "inactive",
  },
  {
    key: "pendingCount",
    amountKey: "pendingAmount",
    label: "Pending",
    titleColor: "#9A7D0A",
    valueColor: "#F39C12",
    filterKey: "freightStatus",
    filterValue: "pending",
  },
  {
    key: "paidCount",
    amountKey: "paidAmount",
    label: "Paid",
    titleColor: "#0B5345",
    valueColor: "#16A085",
    filterKey: "freightStatus",
    filterValue: "paid",
  },
];

const DEFAULT_FREIGHT_COUNTS = FREIGHT_STAT_CARDS.reduce(
  (acc, card) => ({
    ...acc,
    [card.key]: 0,
    ...(card.amountKey ? { [card.amountKey]: 0 } : {}),
  }),
  {},
);

const FreightStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-3 col-sm-6 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const FreightStatCard = ({
  label,
  value,
  amount,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => {
  const showAmount = Number.isFinite(Number(amount));

  return (
  <div className="col-xl-auto col-lg-auto col-md-3 col-sm-6 mb-2 stat-card-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div
          className="stat-title"
          style={{
            color: titleColor,
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {label}
        </div>
        <div
          className="stat-value"
          style={{
            color: valueColor,
            fontSize: "22px",
            fontWeight: "700",
          }}
        >
          {value}
        </div>
        {showAmount && (
          <div
            title={formatFullAmount(amount)}
            style={{
              color: valueColor,
              fontSize: "12px",
              fontWeight: "600",
              marginTop: "2px",
              opacity: 0.9,
              cursor: "default",
            }}
          >
            {formatCompactAmount(amount)}
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

const FrightTracker = () => {
  const savedFilters = JSON.parse(
    sessionStorage.getItem("frightTrackerFilters") || "{}",
  );

  const currentUserRole = (localStorage.getItem("role") || "").toLowerCase();
  const isAdmin = currentUserRole === "admin";

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCountsLoading, setIsCountsLoading] = useState(true);
  const [freightCounts, setFreightCounts] = useState(DEFAULT_FREIGHT_COUNTS);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [statusFilter, setStatusFilter] = useState(
    savedFilters.statusFilter || "",
  );
  const [freightStatusFilter, setFreightStatusFilter] = useState(
    savedFilters.freightStatusFilter || "",
  );
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState({
    ...DEFAULT_FILTER_CHECKS,
    ...(savedFilters.filterChecks || {}),
  });
  const [filterInputs, setFilterInputs] = useState({
    ...DEFAULT_FILTER_INPUTS,
    ...(savedFilters.filterInputs || {}),
  });
  const [activeMultiFilters, setActiveMultiFilters] = useState({
    ...DEFAULT_ACTIVE_MULTI_FILTERS,
    ...(savedFilters.activeMultiFilters || {}),
  });
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [filterSuppliers, setFilterSuppliers] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const [selectedFreightIds, setSelectedFreightIds] = useState([]);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [poModalNumbers, setPoModalNumbers] = useState([]);
  const [poModalFreightNumber, setPoModalFreightNumber] = useState("");
  const [supplierModalNames, setSupplierModalNames] = useState([]);
  const [supplierModalFreightNumber, setSupplierModalFreightNumber] =
    useState("");
  const [deleteFreightId, setDeleteFreightId] = useState("");
  const [deleteFreightNumber, setDeleteFreightNumber] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const loadRequestIdRef = useRef(0);
  const filterPanelRef = useRef(null);

  const anyMultiFilterActive =
    !!activeMultiFilters.customer ||
    !!activeMultiFilters.supplier ||
    !!activeMultiFilters.poNumber ||
    !!activeMultiFilters.createdBy ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  const hasActiveFilters = Boolean(
    activeSearchQuery ||
      anyMultiFilterActive ||
      statusFilter ||
      freightStatusFilter,
  );

  useEffect(() => {
    sessionStorage.setItem(
      "frightTrackerFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        statusFilter,
        freightStatusFilter,
        filterChecks,
        filterInputs,
        activeMultiFilters,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    statusFilter,
    freightStatusFilter,
    filterChecks,
    filterInputs,
    activeMultiFilters,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchFilterOptions = async () => {
      setIsFilterOptionsLoading(true);
      try {
        const currentUserId = localStorage.getItem("id") || undefined;
        const [customersRes, suppliersRes, usersRes] = await Promise.all([
          axios.post(API_URL, {
            action: "get-all-customers",
            page: 1,
            limit: 500,
            currentUserId,
          }),
          axios.post(API_URL, {
            action: "get-active-suppliers",
            currentUserId,
          }),
          axios.post(API_URL, {
            action: "get-freight-users",
            currentUserId,
          }),
        ]);
        if (cancelled) return;

        const customersData = normalizeApiPayload(
          parseApiResponse(customersRes.data),
        );
        const suppliersData = normalizeApiPayload(
          parseApiResponse(suppliersRes.data),
        );
        const usersData = normalizeApiPayload(parseApiResponse(usersRes.data));

        if (isApiSuccess(customersData)) {
          setFilterCustomers(extractCustomerOptions(customersData));
        }
        if (isApiSuccess(suppliersData)) {
          setFilterSuppliers(extractSupplierOptions(suppliersData));
        }
        if (isApiSuccess(usersData) || Array.isArray(usersData?.data)) {
          setFilterUsers(extractUserOptions(usersData));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch freight filter options error:", error);
        }
      } finally {
        if (!cancelled) setIsFilterOptionsLoading(false);
      }
    };

    fetchFilterOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  function toApiDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}-${mm}-${yyyy}`;
  }

  const fetchFreightCounts = useCallback(async () => {
    setIsCountsLoading(true);
    try {
      const currentUserId = localStorage.getItem("id") || undefined;
      const payload = {
        action: "get-freight-counts",
        currentUserId,
      };
      if (activeMultiFilters.fromDate) {
        payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData) && responseData.data) {
        const data = responseData.data;
        setFreightCounts({
          totalCount: Number(data.totalCount) || 0,
          activeCount: Number(data.activeCount) || 0,
          inactiveCount: Number(data.inactiveCount) || 0,
          pendingCount: Number(data.pendingCount) || 0,
          paidCount: Number(data.paidCount) || 0,
          // totalAmount: Number(data.totalAmount) || 0,
          activeAmount: Number(data.activeAmount) || 0,
          // inactiveAmount: Number(data.inactiveAmount) || 0,
          pendingAmount: Number(data.pendingAmount) || 0,
          paidAmount: Number(data.paidAmount) || 0,
        });
      } else {
        setFreightCounts(DEFAULT_FREIGHT_COUNTS);
      }
    } catch (error) {
      console.error("Freight counts error:", error);
      setFreightCounts(DEFAULT_FREIGHT_COUNTS);
    } finally {
      setIsCountsLoading(false);
    }
  }, [activeMultiFilters.fromDate, activeMultiFilters.toDate]);

  const fetchFreight = useCallback(
    async (requestId) => {
      setIsLoading(true);
      try {
        const currentUserId = localStorage.getItem("id") || undefined;
        const payload = {
          action: "get-all-freight",
          page: currentPage,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
          currentUserId,
        };

        const trimmedSearch = activeSearchQuery
          ? activeSearchQuery.trim()
          : "";
        if (trimmedSearch) {
          payload.search = trimmedSearch;
        }

        if (activeMultiFilters.customer) {
          payload.customerId = String(activeMultiFilters.customer).trim();
        }
        if (activeMultiFilters.supplier) {
          payload.supplierId = String(activeMultiFilters.supplier).trim();
        }
        if (activeMultiFilters.poNumber) {
          payload.poNumber = String(activeMultiFilters.poNumber).trim();
        }
        if (activeMultiFilters.createdBy) {
          payload.createdBy = String(activeMultiFilters.createdBy).trim();
        }
        if (activeMultiFilters.fromDate) {
          payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
        }
        if (activeMultiFilters.toDate) {
          payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
        }

        // Mutually exclusive filters (BE contract):
        // - status: "inactive" | "active" → record status only
        // - freightStatus: "pending" | "paid" → among active records only
        const status = String(statusFilter || "")
          .trim()
          .toLowerCase();
        const freightStatus = String(freightStatusFilter || "")
          .trim()
          .toLowerCase();

        if (freightStatus) {
          payload.freightStatus = freightStatus;
        } else if (status) {
          payload.status = status;
        }

        const response = await axios.post(API_URL, payload);
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        const { freight: list, pagination } =
          extractFreightPayload(responseData);

        if (isApiSuccess(responseData) || (Array.isArray(list) && list.length)) {
          setRows(list);
          const total =
            typeof pagination.total === "number"
              ? pagination.total
              : typeof pagination.totalCount === "number"
                ? pagination.totalCount
                : list.length;
          setTotalCount(total);
        } else {
          toast.error(responseData?.message || "Failed to fetch freight", {
            autoClose: 2000,
          });
          setRows([]);
          setTotalCount(0);
        }
      } catch (error) {
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }
        console.error("Fetch freight error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(
          errData?.message || "An error occurred while fetching freight",
          { autoClose: 2000 },
        );
        setRows([]);
        setTotalCount(0);
      } finally {
        if (
          typeof requestId !== "number" ||
          requestId === loadRequestIdRef.current
        ) {
          setIsLoading(false);
        }
      }
    },
    [
      currentPage,
      itemsPerPage,
      activeSearchQuery,
      activeMultiFilters,
      statusFilter,
      freightStatusFilter,
      sortBy,
      sortOrder,
    ],
  );

  useEffect(() => {
    fetchFreightCounts();
  }, [fetchFreightCounts]);

  useEffect(() => {
    const requestId = ++loadRequestIdRef.current;
    fetchFreight(requestId);
  }, [fetchFreight]);

  useEffect(() => {
    setSelectedFreightIds([]);
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activeMultiFilters,
    statusFilter,
    freightStatusFilter,
    sortBy,
    sortOrder,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = rows.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem =
    rows.length > 0 ? Math.min(currentPage * itemsPerPage, totalCount) : 0;

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            { autoClose: 2000 },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) setCurrentPage(1);
      } else if (key === "poNumber") {
        setFilterInputs((inputs) => ({
          ...inputs,
          poNumber:
            inputs.poNumber && inputs.poNumber.trim()
              ? inputs.poNumber
              : DEFAULT_PO_NUMBER_PREFIX,
        }));
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    if (key !== "poNumber") {
      setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const applyPoNumberFilter = () => {
    const trimmed = (filterInputs.poNumber || "").trim();
    const value =
      !trimmed || trimmed === DEFAULT_PO_NUMBER_PREFIX ? "" : trimmed;
    setActiveMultiFilters((prev) => ({ ...prev, poNumber: value }));
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handlePoNumberKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyPoNumberFilter();
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setStatusFilter("");
    setFreightStatusFilter("");
    handleClearFilter();
    setCurrentPage(1);
  };

  const isFreightStatCardActive = (card) => {
    if (!card.filterKey || !card.filterValue) {
      return !statusFilter && !freightStatusFilter;
    }
    if (card.filterKey === "status") {
      return (
        String(statusFilter).toLowerCase() === card.filterValue &&
        !freightStatusFilter
      );
    }
    return (
      String(freightStatusFilter).toLowerCase() === card.filterValue &&
      !statusFilter
    );
  };

  const handleFreightStatCardClick = (card) => {
    if (!card.filterKey || !card.filterValue) {
      setStatusFilter("");
      setFreightStatusFilter("");
      setCurrentPage(1);
      return;
    }

    const nextValue = String(card.filterValue).trim().toLowerCase();

    if (card.filterKey === "status") {
      // Active / Inactive → send only status (e.g. status: "inactive")
      const isSame =
        String(statusFilter).toLowerCase() === nextValue && !freightStatusFilter;
      setStatusFilter(isSame ? "" : nextValue);
      setFreightStatusFilter("");
    } else {
      // Pending / Paid → send only freightStatus (e.g. freightStatus: "pending")
      // BE returns matching freightStatus among active records only
      const isSame =
        String(freightStatusFilter).toLowerCase() === nextValue &&
        !statusFilter;
      setFreightStatusFilter(isSame ? "" : nextValue);
      setStatusFilter("");
    }
    setCurrentPage(1);
  };

  const getRowId = (row) => String(row?._id || row?.id || "").trim();

  const isRowPaid = (row) =>
    String(row?.freightStatus || "")
      .trim()
      .toLowerCase() === "paid";

  const selectableRowIds = rows
    .filter((row) => !isRowPaid(row))
    .map(getRowId)
    .filter(Boolean);

  const allSelectableSelected =
    selectableRowIds.length > 0 &&
    selectableRowIds.every((id) => selectedFreightIds.includes(id));

  const toggleSelectAllVisible = () => {
    if (allSelectableSelected) {
      setSelectedFreightIds((prev) =>
        prev.filter((id) => !selectableRowIds.includes(id)),
      );
      return;
    }
    setSelectedFreightIds((prev) => {
      const next = new Set(prev);
      selectableRowIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const toggleSelectRow = (rowId) => {
    if (!rowId) return;
    setSelectedFreightIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const openPoNumbersModal = (row) => {
    const list = getPoNumbers(row);
    if (list.length <= 1) return;
    setPoModalNumbers(list);
    setPoModalFreightNumber(row?.freightNumber || "");
    if (window.$) {
      window.$("#modalFreightPoNumbers").modal("show");
    }
  };

  const openSuppliersModal = (row) => {
    const list = getSupplierNames(row);
    if (list.length <= 1) return;
    setSupplierModalNames(list);
    setSupplierModalFreightNumber(row?.freightNumber || "");
    if (window.$) {
      window.$("#modalFreightSuppliers").modal("show");
    }
  };

  const handleDeleteClick = (rowId, freightNumber) => {
    setDeleteFreightId(rowId);
    setDeleteFreightNumber(freightNumber || "");
  };

  const handleDelete = async () => {
    if (!deleteFreightId) return;

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
      return;
    }

    const freightIdToDelete = deleteFreightId;
    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-freight",
        id: freightIdToDelete,
        updatedBy: currentUserId,
        currentUserId,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData.message || "Freight deleted successfully",
          { autoClose: 2000 },
        );
        setDeleteFreightId("");
        setDeleteFreightNumber("");
        if (window.$) {
          window.$("#modalDeleteFreight").modal("hide");
        }
        setSelectedFreightIds((prev) =>
          prev.filter((id) => id !== freightIdToDelete),
        );
        fetchFreightCounts();
        const requestId = ++loadRequestIdRef.current;
        fetchFreight(requestId);
      } else {
        toast.error(responseData?.message || "Failed to delete freight", {
          autoClose: 2500,
        });
      }
    } catch (error) {
      console.error("Delete freight error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while deleting freight",
        { autoClose: 2500 },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const openMarkPaidModal = () => {
    if (!isAdmin) {
      toast.error("Access denied. Only admin can mark freight as Paid.", {
        autoClose: 2000,
      });
      return;
    }
    if (selectedFreightIds.length === 0) {
      toast.error("Please select at least one freight record", {
        autoClose: 2000,
      });
      return;
    }
    if (window.$) {
      window.$("#modalMarkFreightPaid").modal("show");
    }
  };

  const closeMarkPaidModal = () => {
    if (window.$) {
      window.$("#modalMarkFreightPaid").modal("hide");
    }
  };

  const confirmMarkPaid = async () => {
    if (!isAdmin) {
      toast.error("Access denied. Only admin can mark freight as Paid.", {
        autoClose: 2000,
      });
      closeMarkPaidModal();
      return;
    }
    if (selectedFreightIds.length === 0) {
      toast.error("Please select at least one freight record", {
        autoClose: 2000,
      });
      closeMarkPaidModal();
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      closeMarkPaidModal();
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
      return;
    }

    setIsMarkingPaid(true);
    try {
      const response = await axios.post(API_URL, {
        action: "mark-freight-paid",
        ids: selectedFreightIds,
        currentUserId,
        updatedBy: currentUserId,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData.message || "Freight marked as paid successfully",
          { autoClose: 2000 },
        );
        setSelectedFreightIds([]);
        closeMarkPaidModal();
        fetchFreightCounts();
        const requestId = ++loadRequestIdRef.current;
        fetchFreight(requestId);
      } else {
        toast.error(
          responseData?.message || "Failed to mark freight as paid",
          { autoClose: 2500 },
        );
      }
    } catch (error) {
      console.error("Mark freight paid error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while marking freight as paid",
        { autoClose: 2500 },
      );
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleSort = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1 text-muted"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className={`fa-solid ${
          sortOrder === "asc" ? "fa-sort-up" : "fa-sort-down"
        } ml-1`}
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      style={{
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        ...extraStyle,
      }}
      onClick={() => handleSort(column)}
    >
      <span className="d-inline-flex align-items-center">
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Freight Tracker</h1>
            </div>
            <div className="col-sm-6 text-right">
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-success mr-2"
                  onClick={openMarkPaidModal}
                  disabled={isMarkingPaid || selectedFreightIds.length === 0}
                >
                  <i
                    className={`fa-solid ${
                      isMarkingPaid ? "fa-spinner fa-spin" : "fa-check"
                    } mr-1`}
                  />
                  {isMarkingPaid
                    ? "Updating..."
                    : selectedFreightIds.length > 0
                      ? `Paid (${selectedFreightIds.length})`
                      : "Paid"}
                </button>
              )}
              <Link to="/frightTrackerCreate" className="btn btn-blue">
                <i className="fa-solid fa-plus mr-1" />
                Create Freight
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isCountsLoading
                  ? FREIGHT_STAT_CARDS.map((card) => (
                      <FreightStatCardSkeleton key={card.key} />
                    ))
                  : FREIGHT_STAT_CARDS.map((card) => (
                      <FreightStatCard
                        key={card.key}
                        label={card.label}
                        value={freightCounts[card.key]}
                        amount={
                          card.amountKey
                            ? freightCounts[card.amountKey]
                            : undefined
                        }
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isFreightStatCardActive(card)}
                        onClick={() => handleFreightStatCardClick(card)}
                      />
                    ))}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search freight..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Filter
                        </label>
                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() =>
                            setIsFilterPanelOpen((open) => !open)
                          }
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {anyMultiFilterActive
                              ? "Filters"
                              : "Select Filters"}
                          </span>
                          <i
                            className={`fa-solid fa-chevron-${
                              isFilterPanelOpen ? "up" : "down"
                            }`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          />
                        </button>

                        {isFilterPanelOpen && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "190px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            {FREIGHT_FILTER_OPTIONS.map((filter) => {
                              const activeCount = Object.values(
                                filterChecks,
                              ).filter(Boolean).length;

                              return (
                                <div
                                  className="custom-control custom-checkbox mb-2"
                                  key={filter.key}
                                >
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={filter.id}
                                    checked={!!filterChecks[filter.key]}
                                    disabled={
                                      !filterChecks[filter.key] &&
                                      activeCount >= MAX_ACTIVE_FILTERS
                                    }
                                    onChange={() =>
                                      handleFilterCheckToggle(filter.key)
                                    }
                                  />
                                  <label
                                    className="custom-control-label"
                                    htmlFor={filter.id}
                                  >
                                    {filter.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {filterChecks.customer && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Customer
                          </label>
                          <select
                            className="form-control"
                            value={filterInputs.customer}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "customer",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isFilterOptionsLoading &&
                              filterCustomers.length === 0
                                ? "Loading customers..."
                                : "All customers"}
                            </option>
                            {filterCustomers.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.supplier && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Supplier
                          </label>
                          <select
                            className="form-control"
                            value={filterInputs.supplier}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "supplier",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isFilterOptionsLoading &&
                              filterSuppliers.length === 0
                                ? "Loading suppliers..."
                                : "All suppliers"}
                            </option>
                            {filterSuppliers.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.poNumber && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            PO Number
                          </label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder={DEFAULT_PO_NUMBER_PREFIX}
                              value={filterInputs.poNumber}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "poNumber",
                                  e.target.value,
                                )
                              }
                              onKeyPress={handlePoNumberKeyPress}
                            />
                            <div className="input-group-append">
                              <div
                                className="input-group-text crsr_pntr"
                                onClick={applyPoNumberFilter}
                                style={{ cursor: "pointer" }}
                                title="Apply PO filter"
                              >
                                <i className="fa-solid fa-search" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {filterChecks.createdBy && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Created User
                          </label>
                          <select
                            className="form-control"
                            value={filterInputs.createdBy}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "createdBy",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isFilterOptionsLoading &&
                              filterUsers.length === 0
                                ? "Loading users..."
                                : "All users"}
                            </option>
                            {filterUsers.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.dateRange && (
                      <>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              From Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.fromDate || ""}
                              max={filterInputs.toDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "fromDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              To Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.toDate || ""}
                              min={filterInputs.fromDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "toDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {hasActiveFilters && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearAll}
                          style={{
                            minWidth: "75px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear
                        </button>
                      </div>
                    )}
                  </div>

                  {isLoading && rows.length === 0 ? (
                    <div className="text-center p-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  ) : rows.length === 0 && !hasActiveFilters ? (
                    <div className="nocontent_div text-center p-4">
                      No freight records found.
                      <br />
                      <div className="mt-3">
                        <Link to="/frightTrackerCreate" className="btn btn-blue">
                          Create Freight
                        </Link>
                      </div>
                    </div>
                  ) : rows.length === 0 && hasActiveFilters ? (
                    <div className="nocontent_div text-center p-4">
                      No freight found matching your filters.
                      <br />
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={handleClearAll}
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="supply-table-container"
                      style={{ position: "relative" }}
                    >
                      {isLoading && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.55)",
                            pointerEvents: "none",
                          }}
                        >
                          <PulseLoader color="#1199B2" size={10} />
                        </div>
                      )}
                      <div
                        className="table-responsive"
                        style={{
                          opacity: isLoading ? 0.6 : 1,
                          transition: "opacity 0.15s ease",
                          pointerEvents: isLoading ? "none" : "auto",
                        }}
                      >
                        <table
                          id="freightTracker"
                          width="100%"
                          className="table table-striped table-bordered txt_lft"
                        >
                          <thead>
                            <tr>
                              {isAdmin && (
                                <th
                                  style={{
                                    width: "40px",
                                    minWidth: "40px",
                                    textAlign: "center",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={allSelectableSelected}
                                    onChange={toggleSelectAllVisible}
                                    disabled={
                                      selectableRowIds.length === 0 ||
                                      isMarkingPaid
                                    }
                                    title="Select all visible"
                                  />
                                </th>
                              )}
                              <th
                                style={{
                                  whiteSpace: "nowrap",
                                  width: "40px",
                                  minWidth: "40px",
                                }}
                              >
                                S.No
                              </th>
                              {renderSortableHeader(
                                "freightNumber",
                                "Freight No",
                                { minWidth: "120px" },
                              )}
                              {renderSortableHeader("customerName", "Customer", {
                                minWidth: "140px",
                              })}
                              <th style={{ minWidth: "180px" }}>Description</th>
                              {renderSortableHeader("freightAmount", "Amount", {
                                minWidth: "100px",
                              })}
                              {renderSortableHeader(
                                "totalPoValue",
                                "Total PO Value",
                                { minWidth: "120px" },
                              )}
                              {renderSortableHeader("freightQty", "Qty", {
                                minWidth: "70px",
                              })}
                              <th style={{ minWidth: "140px" }}>PO Numbers</th>
                              {renderSortableHeader(
                                "supplierName",
                                "Supplier",
                                { minWidth: "130px" },
                              )}
                              {renderSortableHeader(
                                "freightStatus",
                                "Status",
                                { minWidth: "100px" },
                              )}
                              <th style={{ minWidth: "120px" }}>Created By</th>
                              {renderSortableHeader("createdAt", "Created At", {
                                minWidth: "140px",
                              })}
                              <th style={{ width: "90px", minWidth: "90px" }}>
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, index) => {
                              const id = getRowId(row);
                              const paid = isRowPaid(row);
                              return (
                                <tr key={id || index}>
                                  {isAdmin && (
                                    <td style={{ textAlign: "center" }}>
                                      <input
                                        type="checkbox"
                                        checked={
                                          Boolean(id) &&
                                          selectedFreightIds.includes(id)
                                        }
                                        onChange={() => toggleSelectRow(id)}
                                        disabled={!id || paid || isMarkingPaid}
                                        title={
                                          paid
                                            ? "Already paid"
                                            : "Select freight"
                                        }
                                      />
                                    </td>
                                  )}
                                  <td>
                                    {(currentPage - 1) * itemsPerPage +
                                      index +
                                      1}
                                  </td>
                                  <td style={{ whiteSpace: "nowrap" }}>
                                    {row.freightNumber || "-"}
                                  </td>
                                  <td>{row.customerName || "-"}</td>
                                  <td
                                    style={{
                                      maxWidth: "220px",
                                      wordWrap: "break-word",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {row.freightDescription || "-"}
                                  </td>
                                  <td className="text-right">
                                    {formatAmount(row.freightAmount)}
                                  </td>
                                  <td className="text-right">
                                    {formatAmount(row.totalPoValue)}
                                  </td>
                                  <td className="text-right">
                                    {row.freightQty ?? "-"}
                                  </td>
                                  <td
                                    style={{
                                      maxWidth: "180px",
                                      wordWrap: "break-word",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {(() => {
                                      const poList = getPoNumbers(row);
                                      if (poList.length === 0) return "-";
                                      if (poList.length === 1) return poList[0];
                                      const remaining = poList.length - 1;
                                      return (
                                        <span
                                          className="d-inline-flex align-items-center flex-wrap"
                                          style={{ gap: "4px" }}
                                        >
                                          <span>{poList[0]}</span>
                                          <button
                                            type="button"
                                            className="btn btn-link p-0 align-baseline"
                                            style={{
                                              fontWeight: 700,
                                              color: "#1199B2",
                                              textDecoration: "none",
                                              lineHeight: 1,
                                            }}
                                            onClick={() =>
                                              openPoNumbersModal(row)
                                            }
                                            title="View all PO numbers"
                                          >
                                            +{remaining}
                                          </button>
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td
                                    style={{
                                      maxWidth: "180px",
                                      wordWrap: "break-word",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {(() => {
                                      const supplierList = getSupplierNames(row);
                                      if (supplierList.length === 0) return "-";
                                      if (supplierList.length === 1) {
                                        return supplierList[0];
                                      }
                                      const remaining = supplierList.length - 1;
                                      return (
                                        <span
                                          className="d-inline-flex align-items-center flex-wrap"
                                          style={{ gap: "4px" }}
                                        >
                                          <span>{supplierList[0]}</span>
                                          <button
                                            type="button"
                                            className="btn btn-link p-0 align-baseline"
                                            style={{
                                              fontWeight: 700,
                                              color: "#1199B2",
                                              textDecoration: "none",
                                              lineHeight: 1,
                                            }}
                                            onClick={() =>
                                              openSuppliersModal(row)
                                            }
                                            title="View all suppliers"
                                          >
                                            +{remaining}
                                          </button>
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {freightStatusBadge(row.freightStatus)}
                                  </td>
                                  <td>{displayUser(row.createdBy)}</td>
                                  <td style={{ whiteSpace: "nowrap" }}>
                                    {formatDate(row.createdAt)}
                                  </td>
                                  <td>
                                    <div
                                      align="center"
                                      style={{ whiteSpace: "nowrap" }}
                                      className="d-flex justify-content-center align-items-center"
                                    >
                                      <Link
                                        to={`/frightTrackerView/${id}`}
                                        className="crsr_pntr mr-2"
                                        title="View"
                                      >
                                        <i className="fa-solid fa-eye" />
                                      </Link>
                                      {!paid && (
                                        <>
                                          <Link
                                            to={`/frightTrackerEdit/${id}`}
                                            className="crsr_pntr mr-2"
                                            title="Edit"
                                          >
                                            <i className="fa-solid fa-pen-to-square" />
                                          </Link>
                                          <a
                                            href="#modalDeleteFreight"
                                            className="crsr_pntr action-icon-link"
                                            title="Delete"
                                            data-toggle="modal"
                                            data-target="#modalDeleteFreight"
                                            onClick={() =>
                                              handleDeleteClick(
                                                id,
                                                row.freightNumber,
                                              )
                                            }
                                          >
                                            <i className="fa-solid fa-trash-can" />
                                          </a>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {rows.length > 0 && (
                        <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                          <div className="mb-2 mb-md-0">
                            <select
                              className="form-control form-control-sm d-inline-block"
                              style={{ width: "auto" }}
                              value={itemsPerPage}
                              onChange={handleItemsPerPageChange}
                            >
                              {[10, 25, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                            <span className="ml-2 text-muted">
                              {startItem}–{endItem} of {totalCount}
                            </span>
                          </div>

                          <nav>
                            <ul className="pagination pagination-sm mb-0">
                              <li
                                className={`page-item ${
                                  currentPage === 1 ? "disabled" : ""
                                }`}
                              >
                                <button
                                  type="button"
                                  className="page-link"
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                  disabled={currentPage === 1}
                                >
                                  Prev
                                </button>
                              </li>
                              {getPageNumbers().map((page, idx) =>
                                page === "ellipsis" ? (
                                  <li
                                    key={`e-${idx}`}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">…</span>
                                  </li>
                                ) : (
                                  <li
                                    key={page}
                                    className={`page-item ${
                                      currentPage === page ? "active" : ""
                                    }`}
                                  >
                                    <button
                                      type="button"
                                      className="page-link"
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                ),
                              )}
                              <li
                                className={`page-item ${
                                  currentPage === totalPages ? "disabled" : ""
                                }`}
                              >
                                <button
                                  type="button"
                                  className="page-link"
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                  disabled={currentPage === totalPages}
                                >
                                  Next
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalFreightPoNumbers"
        tabIndex={-1}
        aria-labelledby="modalFreightPoNumbersLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalFreightPoNumbersLabel">
                PO Numbers
                {poModalFreightNumber ? ` - ${poModalFreightNumber}` : ""}
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {poModalNumbers.length === 0 ? (
                <div className="text-center text-muted">No PO numbers</div>
              ) : (
                <ul className="list-group list-group-flush mb-0">
                  {poModalNumbers.map((poNo, index) => (
                    <li
                      key={`${poNo}-${index}`}
                      className="list-group-item px-0 d-flex align-items-center"
                    >
                      <span className="mr-2 text-muted">{index + 1}.</span>
                      <span className="fwt_600">{poNo}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalFreightSuppliers"
        tabIndex={-1}
        aria-labelledby="modalFreightSuppliersLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalFreightSuppliersLabel">
                Suppliers
                {supplierModalFreightNumber
                  ? ` - ${supplierModalFreightNumber}`
                  : ""}
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {supplierModalNames.length === 0 ? (
                <div className="text-center text-muted">No suppliers</div>
              ) : (
                <ul className="list-group list-group-flush mb-0">
                  {supplierModalNames.map((name, index) => (
                    <li
                      key={`${name}-${index}`}
                      className="list-group-item px-0 d-flex align-items-center"
                    >
                      <span className="mr-2 text-muted">{index + 1}.</span>
                      <span className="fwt_600">{name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalDeleteFreight"
        tabIndex={-1}
        aria-labelledby="modalDeleteFreightLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalDeleteFreightLabel">
                Delete Freight
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">
                  {deleteFreightNumber || "this freight"}
                </span>
                ?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div
          className="modal fade"
          id="modalMarkFreightPaid"
          tabIndex={-1}
          aria-labelledby="modalMarkFreightPaidLabel"
          aria-hidden="true"
          data-backdrop="static"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="modalMarkFreightPaidLabel">
                  Mark as Paid
                </h5>
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                  disabled={isMarkingPaid}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
              <div className="modal-body">
                <div className="w-100 d-inline-block text-center">
                  Are You sure want to change the status Paid?
                  {selectedFreightIds.length > 0 ? (
                    <>
                      <br />
                      <span className="fwt_600">
                        {selectedFreightIds.length} record
                        {selectedFreightIds.length > 1 ? "s" : ""} selected
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  data-dismiss="modal"
                  disabled={isMarkingPaid}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmMarkPaid}
                  disabled={isMarkingPaid}
                >
                  {isMarkingPaid ? "Updating..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrightTracker;
